import {
  Actions,
  createSelector,
  getActionTypeFromInstance,
  getStoreMetadata,
  StateContext,
} from '@ngxs/store';
import { MetaDataModel } from '@ngxs/store/src/internal/internals';
import { filter, first } from 'rxjs/operators';

import { ENTITY_STATE_ACTIONS } from './entity-state-actions';
import { NGXS_META_KEY } from './helpers';
import {
  ActionClassType,
  ActionInstance,
  ActionStatus,
  ActionType,
  EntityId,
  EntityType,
  IEntityState,
  IRepo,
  IRepoInstance,
  MetaData,
  SaveOptions,
} from './interfaces';

type RepoContext = StateContext<IEntityState<EntityType, MetaData>>;

abstract class Repo implements IRepoInstance<any, any> {
  public static get activeEntity(): (...args: any) => EntityType {
    return createSelector(
      [this.activeId, this.withRelations()],
      (activeId, entities) => entities[activeId],
    );
  }

  public static get entities(): (...args: any) => EntityType[] | null {
    return createSelector(
      [this.rawEntities, this.rawIds, this.lastUpdated],
      (entities: any, ids: number[], lastUpdated: any) =>
        lastUpdated === 0 ? null : ids.map(id => entities[id]),
    );
  }

  public static get meta(): (...args: any) => any {
    return createSelector([this.rawMeta], meta => meta);
  }

  public static get latestEntity(): (...args: any) => EntityType {
    return createSelector(
      [this.withRelations(), this.latestId],
      (entities, latestId) => entities[latestId],
    );
  }

  public static isLoading(
    action: ActionInstance | ActionClassType | ActionType,
  ): (...args: any) => boolean {
    return createSelector([this], (state: IEntityState<EntityType>) => {
      const type =
        typeof action === 'string' ? action : getActionTypeFromInstance(action) || action.type;

      return !!state.loading.find(loadingAction => loadingAction === type);
    });
  }

  public static get lastUpdated(): (state: IEntityState<EntityType>) => number | undefined {
    return createSelector([this], (state: IEntityState<EntityType>) => state.lastUpdated);
  }

  public static get activeId(): (
    state: IEntityState<EntityType>,
  ) => number | string | null | undefined {
    return createSelector([this], (state: IEntityState<EntityType>) => state.active);
  }

  public static get latestId(): (state: IEntityState<EntityType>) => number | string | undefined {
    return createSelector([this], (state: IEntityState<EntityType>) => state.latestId);
  }

  protected static get rawMeta(): (...args: any) => EntityId {
    return createSelector([this], (state: IEntityState<EntityType>) => state.meta);
  }

  protected static get rawEntities(): (...args: any) => Record<EntityId, EntityType> {
    return createSelector([this], state => state.entities);
  }

  protected static get rawIds(): (state: IEntityState<EntityType>) => (number | string)[] {
    return createSelector([this], (state: IEntityState<EntityType>) => state.ids);
  }

  protected static withRelations(): (...args: any) => Record<EntityId, EntityType> {
    return createSelector([this], state => state.entities);
  }

  protected static get withRelationsAsArray(): (...args: any) => EntityType[] | null {
    return createSelector(
      [this.withRelations(), this.rawIds, this.lastUpdated],
      (entities: EntityType[], ids: number[], lastUpdated: number) =>
        lastUpdated === 0 ? null : ids.map(id => entities[id]),
    );
  }

  protected constructor(
    private actions: Actions,
    private updateStrategy: (oldEntity: EntityType, newEntity: EntityType) => EntityType,
  ) {
    this.setupListeners();
  }

  public save(
    ctx: RepoContext,
    action: ActionInstance,
    entity: EntityType,
    options?: SaveOptions,
  ): void {
    this.saveMany(ctx, action, [entity], options);
  }

  public saveMany(
    ctx: RepoContext,
    action: ActionInstance,
    entities: EntityType[],
    options?: SaveOptions,
  ): void {
    const { position = 'end', preservePositionOnUpdate = true } = options || {};
    let state = ctx.getState();
    const newIds = this.createNewIds(state.ids, entities, { position, preservePositionOnUpdate });
    const newEntities = <EntityType[]>(
      entities.reduce((acc, entity) => ({ ...acc, [entity.id]: entity }), {})
    );
    const updatedEntities = newIds.reduce((acc, id) => {
      const newEntity = state.entities[id]
        ? this.updateStrategy(state.entities[id], newEntities[id])
        : newEntities[id];

      return { ...acc, [id]: newEntity };
    }, state.entities);

    this.actions
      .pipe(
        filter(
          wrappedAction =>
            getActionTypeFromInstance(wrappedAction.action) === getActionTypeFromInstance(action) &&
            ActionStatus.Successful === wrappedAction.status,
        ),
        first(),
      )
      .subscribe(() => {
        const actionType = getActionTypeFromInstance(action);
        state = ctx.getState();

        ctx.patchState({
          error: Object.entries(state.error).reduce(
            (acc, [key, value]) => (key === actionType ? acc : { ...acc, [key]: value }),
            {},
          ),
        });
      });

    ctx.patchState({
      ids: newIds,
      entities: updatedEntities,
      latestId: newIds[newIds.length - 1],
      lastUpdated: Date.now(),
    });
  }

  public remove(ctx: RepoContext, entity: EntityId | EntityType): void {
    const entityId = typeof entity === 'object' ? entity.id : entity;
    const state = ctx.getState();
    const newEntities = Object.entries(state.entities).reduce(
      (acc, [key, value]) => (value.id === entityId ? acc : { ...acc, [key]: value }),
      {},
    );

    ctx.patchState({
      ids: state.ids.filter(_id => entityId !== _id),
      entities: newEntities,
      lastUpdated: Date.now(),
    });
  }

  public markAsLoading(ctx: RepoContext, action: ActionInstance): void {
    let state = ctx.getState();
    const actionType = action.type || getActionTypeFromInstance(action);

    ctx.patchState({
      loading: [...state.loading, actionType],
    });

    this.actions
      .pipe(
        filter(
          wrappedAction =>
            wrappedAction.action === action &&
            (ActionStatus.Successful === wrappedAction.status ||
              ActionStatus.Errored === wrappedAction.status ||
              ActionStatus.Canceled === wrappedAction.status),
        ),
        first(),
      )
      .subscribe(() => {
        state = ctx.getState();

        ctx.patchState({
          loading: state.loading.filter(actionLoading => actionLoading !== actionType),
        });
      });
  }

  public markAsActive(ctx: RepoContext, id: EntityId | null): void {
    ctx.patchState({
      active: id,
    });
  }

  public setError(ctx: RepoContext, action: ActionInstance, error: any): void {
    const state = ctx.getState();
    const actionType = getActionTypeFromInstance(action)!;

    ctx.patchState({
      error: {
        ...state.error,
        [actionType]: error,
      },
    });
  }

  public resetStateToDefaults(ctx: RepoContext): void {
    const metadata = getStoreMetadata(<any>this.constructor);
    ctx.setState(metadata.defaults);
  }

  public updateMetadata(ctx: RepoContext, metadata: Partial<MetaData>): void {
    const state = ctx.getState();
    const oldMetadata = state.meta || {};
    ctx.patchState({
      ...state,
      meta: {
        ...oldMetadata,
        ...metadata,
      },
    });
  }

  private createNewIds(
    currentIds: EntityId[],
    entities: EntityType[],
    { position, preservePositionOnUpdate }: SaveOptions,
  ): EntityId[] {
    const newIds = entities.map(entity => entity.id);
    const tempIds = position === 'end' ? [...currentIds, ...newIds] : [...newIds, ...currentIds];

    const reduceRight = () =>
      tempIds.reduceRight<number[]>((acc, id) => (acc.includes(id) ? acc : [id, ...acc]), []);
    const reduceLeft = () =>
      tempIds.reduce<number[]>((acc, id) => (acc.includes(id) ? acc : [...acc, id]), []);

    if (preservePositionOnUpdate) {
      if (position === 'end') return reduceLeft();
      if (position === 'start') return reduceRight();
    } else {
      if (position === 'end') return reduceRight();
      if (position === 'start') return reduceLeft();
    }

    return reduceLeft();
  }

  private setupListeners(): void {
    // @ts-ignore
    const { path, actions } = <MetaDataModel>this.constructor[NGXS_META_KEY];
    for (const { actionName: name, fnName } of Object.values(ENTITY_STATE_ACTIONS)) {
      const actionName = `[${path}] ${name}`;
      actions[actionName] = [
        {
          fn: fnName,
          options: {},
          type: actionName,
        },
      ];
    }
  }

  private setActiveHandler(ctx: RepoContext, action: { payload: any }): void {
    this.markAsActive(ctx, action.payload);
  }

  private saveManyHandler(ctx: RepoContext, action: { payload: any }): void {
    this.saveMany(ctx, action, action.payload);
  }

  private clearHandler(ctx: RepoContext): void {
    this.resetStateToDefaults(ctx);
  }
}

export const EntityState = <T extends { id: EntityId }, M = any>(): IRepo<T, M> => <any>Repo;
